/** One entry of Google Places `address_components`. */
export interface GoogleAddressComponent {
  long_name: string;
  short_name: string;
  types: Array<string>;
}

/** Street / city / state / ZIP split out of a Google place, for billing tax and search. */
export interface StructuredAddress {
  address1?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  country?: string;
}

/**
 * Splits Google Places `address_components` into the parts the backend stores
 * (business.address1 / address_city / address_state / address_zipcode). State and
 * country are the short codes ("WA", "US"), which is what Stripe Tax expects.
 */
export function parseAddressComponents(
  components: Array<GoogleAddressComponent> | null | undefined,
): StructuredAddress {
  if (!components?.length) return {};
  const find = (type: string) => components.find((c) => c.types.includes(type));

  const streetNumber = find("street_number")?.long_name;
  const route = find("route")?.short_name;
  const subpremise = find("subpremise")?.long_name;
  const street = [streetNumber, route].filter(Boolean).join(" ");
  const address1 = street
    ? subpremise
      ? `${street} #${subpremise}`
      : street
    : (find("premise")?.long_name ?? find("point_of_interest")?.long_name);

  const city =
    find("locality")?.long_name ??
    find("postal_town")?.long_name ??
    find("sublocality")?.long_name ??
    find("administrative_area_level_3")?.long_name;
  const state = find("administrative_area_level_1")?.short_name;
  const zip = find("postal_code")?.long_name;
  const zipSuffix = find("postal_code_suffix")?.long_name;
  const country = find("country")?.short_name;

  return {
    address1: address1 || undefined,
    city: city || undefined,
    state: state || undefined,
    zipcode: zip ? (zipSuffix ? `${zip}-${zipSuffix}` : zip) : undefined,
    country: country || undefined,
  };
}
